import { cn } from "@/lib/utils";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { MIN_SEARCH_LENGTH } from "@/components/estimation-details/Rooms/hooks/useLineItemOptions";

export interface FindSelectOptions {
  value: string;
  label: string;
  parent?: string;
  /** Xactimate codes, e.g. "DRY | DERA" */
  badge?: string;
  /**
   * The search query whose individual words should be bolded wherever they
   * appear in `label`. Each word is highlighted independently so split
   * matches like "walls … paint" are both emphasised even when non-adjacent.
   */
  matchSegment?: string;
}

function FindSelectOptionLabel({ label, matchSegment }: { label: string; matchSegment?: string }) {
  if (!matchSegment?.trim()) {
    return <span className="text-left">{label}</span>;
  }

  const words = matchSegment.trim().split(/\s+/).filter(Boolean);
  const lowerLabel = label.toLowerCase();

  // Collect [start, end) ranges for every word occurrence in the label.
  const ranges: Array<[number, number]> = [];
  for (const word of words) {
    const lowerWord = word.toLowerCase();
    let pos = 0;
    while (pos < lowerLabel.length) {
      const idx = lowerLabel.indexOf(lowerWord, pos);
      if (idx === -1) break;
      ranges.push([idx, idx + word.length]);
      pos = idx + word.length;
    }
  }

  if (ranges.length === 0) {
    return <span className="text-left">{label}</span>;
  }

  // Sort then merge overlapping / adjacent ranges.
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [s, e] of ranges) {
    if (merged.length === 0 || s > merged[merged.length - 1][1]) {
      merged.push([s, e]);
    } else {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    }
  }

  // Build alternating plain / bold segments.
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (cursor < s) parts.push(label.slice(cursor, s));
    parts.push(
      <span key={s} className="font-semibold">
        {label.slice(s, e)}
      </span>,
    );
    cursor = e;
  }
  if (cursor < label.length) parts.push(label.slice(cursor));

  return <span className="text-left">{parts}</span>;
}

interface FindSelectProps {
  label?: string;
  error?: string;
  options: FindSelectOptions[];
  onChange?: (id: string, value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  onCreateItem?: (lineItemText?: string) => void;
  onFindOptions: (searchTerm: string) => void;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  defaultValue?: string;
  onClear?: () => void;
  autoFocus?: boolean;
  showOptions?: "top" | "bottom";
  inputClassName?: string;
}

export const FindSelect = forwardRef<HTMLDivElement, FindSelectProps>(
  (
    {
      label,
      error,
      options,
      placeholder = "Search for an option",
      onFindOptions,
      onChange,
      onBlur,
      searchTerm,
      setSearchTerm,
      onCreateItem,
      autoFocus = false,
      showOptions = "top",
      onClear,
      inputClassName,
    }: FindSelectProps,
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const inputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
      setFilteredOptions(options);
    }, [options]);

    const handleSelectOption = (option: FindSelectOptions) => {
      setSearchTerm(option.label);
      setIsOpen(false);
      onChange?.(option.value, option.label);
      inputRef.current?.blur();
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      setSearchTerm("");
      onFindOptions("");
      onChange?.("", "");
      setIsOpen(false);
      onClear?.();
      inputRef.current?.focus();
    };

    const trimmedSearch = searchTerm.trim();
    const isQueryTooShortForSearch = trimmedSearch.length > 0 && trimmedSearch.length < MIN_SEARCH_LENGTH;
    const tooShortMessage = `Type at least ${MIN_SEARCH_LENGTH} characters to search.`;

    const groupedOptions = filteredOptions.reduce<Record<string, FindSelectOptions[]>>((acc, option) => {
      const group = option.parent ?? "Ungrouped";
      if (!acc[group]) acc[group] = [];
      acc[group].push(option);
      return acc;
    }, {});

    // Flat array in the same order options are visually rendered (group by group)
    const visuallyOrderedOptions = Object.values(groupedOptions).flat();

    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
    const listRef = useRef<HTMLDivElement>(null);

    const optionIndexMap = useMemo(() => {
      const map = new Map<string, number>();
      visuallyOrderedOptions.forEach((opt, i) => map.set(opt.value, i));
      return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredOptions]);

    useEffect(() => {
      setHighlightedIndex(-1);
    }, [filteredOptions]);

    useEffect(() => {
      const option = optionRefs.current[highlightedIndex];
      const list = listRef.current;
      if (!option || !list) return;
      const optTop = option.offsetTop;
      const optBottom = optTop + option.offsetHeight;
      const listTop = list.scrollTop;
      const listBottom = listTop + list.clientHeight;
      if (optTop < listTop) {
        list.scrollTop = optTop;
      } else if (optBottom > listBottom) {
        list.scrollTop = optBottom - list.clientHeight;
      }
    }, [highlightedIndex]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (searchTerm.length > 0) setIsOpen(true);
        if (visuallyOrderedOptions.length > 0) {
          setHighlightedIndex((prev) => (prev + 1) % visuallyOrderedOptions.length);
        }
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (visuallyOrderedOptions.length > 0) {
          setHighlightedIndex((prev) => (prev <= 0 ? visuallyOrderedOptions.length - 1 : prev - 1));
        }
      } else if (e.key === "Enter" && isOpen && highlightedIndex >= 0 && visuallyOrderedOptions[highlightedIndex]) {
        e.preventDefault();
        e.stopPropagation();
        handleSelectOption(visuallyOrderedOptions[highlightedIndex]);
      }
    };

    const [triggerWidth, setTriggerWidth] = useState(0);
    useEffect(() => {
      if (triggerRef.current) {
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
      const resizeObserver = new ResizeObserver(() => {
        if (triggerRef.current) {
          setTriggerWidth(triggerRef.current.offsetWidth);
        }
      });
      if (triggerRef.current) {
        resizeObserver.observe(triggerRef.current);
      }
      return () => resizeObserver.disconnect();
    }, []);

    return (
      <div className="flex w-full flex-col space-y-1" ref={ref}>
        {label && <p className="text-sm font-medium text-gray-700">{label}</p>}

        <Popover
          open={isOpen && searchTerm.length > 0}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) {
              onBlur?.();
            }
          }}
        >
          <PopoverTrigger asChild ref={triggerRef}>
            <div className="relative flex items-center">
              <Input
                ref={inputRef}
                autoFocus={autoFocus}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder={placeholder}
                value={searchTerm}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                  const value = e.target.value;
                  setIsOpen(true);
                  setSearchTerm(value);
                  onFindOptions(value);
                }}
                className={cn(
                  inputClassName ?? "h-7 w-full rounded-lg border p-2 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none",
                  !inputClassName && (error ? "border-orange-500 bg-orange-50" : "border-gray-300"),
                )}
              />
              {searchTerm && (
                <div
                  className="absolute top-1/2 right-2 -translate-y-1/2 transform cursor-pointer"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleClear}
                >
                  <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </div>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            side={showOptions === "top" ? "top" : "bottom"}
            align="start"
            className="find-select-options w-auto border-gray-200 !bg-[#fff] p-0 !text-[#000] shadow-lg"
            style={{ width: triggerWidth }}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (triggerRef.current?.contains(e.target as Node)) {
                e.preventDefault();
              }
            }}
          >
            <div ref={listRef} className="max-h-60 overflow-y-auto" onMouseDown={(e) => e.stopPropagation()}>
              {filteredOptions.length === 0 ? (
                <div className="flex flex-col">
                  {isQueryTooShortForSearch ? (
                    <div className="p-5 text-sm text-gray-500">{tooShortMessage}</div>
                  ) : (
                    <>
                      <div className="p-5 text-sm text-gray-500">No options found</div>
                      {onCreateItem && searchTerm.trim().length > 0 && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onCreateItem(searchTerm);
                            setIsOpen(false);
                          }}
                          className="find-select-options flex cursor-pointer items-center gap-2 border-t border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-50"
                        >
                          <Plus className="h-4 w-4" />
                          <span>
                            Create <span className="font-semibold">"{searchTerm}"</span>
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                Object.entries(groupedOptions).map(([parent, group]) => (
                  <div key={parent} className="border-t border-gray-100 first:border-t-0">
                    <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">
                      {parent === "Ungrouped" ? "Suggestions" : parent}
                    </div>
                    <div className="flex flex-col">
                      {group.map((option) => {
                        const globalIndex = optionIndexMap.get(option.value) ?? -1;
                        const isHighlighted = globalIndex === highlightedIndex;
                        return (
                          <div
                            key={option.value}
                            ref={(el) => {
                              optionRefs.current[globalIndex] = el;
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              handleSelectOption(option);
                            }}
                            className={cn(
                              "find-select-options flex cursor-pointer items-start justify-between gap-2 px-3 py-2 text-sm transition-colors",
                              isHighlighted ? "bg-gray-100" : "hover:bg-gray-100",
                            )}
                          >
                            <FindSelectOptionLabel label={option.label} matchSegment={option.matchSegment} />
                            {option.badge ? (
                              <span className="shrink-0 rounded-full bg-stone-100 px-2 py-0.5 font-mono text-xs text-stone-600 dark:bg-stone-800 dark:text-stone-300">
                                {option.badge}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </PopoverContent>
        </Popover>
        {error && <p className="text-sm text-orange-500">{error}</p>}
      </div>
    );
  },
);
